import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowBack } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, Fade, LinearProgress, Stack, Typography } from '@mui/material';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import { 
    ResetPasswordFormData, 
    resetPasswordSchema 
} from '../../utils/validation/auth.schema';
import FormTextField from './FormTextField';
import AuthLayout from './AuthLayout';
import { getAuth, confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import PasswordStrengthIndicator from './PasswordStrengthIndicator';
import { useAuth } from '../../context/AuthContext';
import { formatFirebaseAuthError } from '../../utils/errorHandling';

const ResetPasswordPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { loginAfterReset } = useAuth();
    const [errorKey, setErrorKey] = useState<string | null>(null);
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [emailVerified, setEmailVerified] = useState(false);
    const [email, setEmail] = useState('');
    const [oobCode, setOobCode] = useState('');
    const [redirectProgress, setRedirectProgress] = useState(0);
    const [isRedirecting, setIsRedirecting] = useState(false);
    
    const {
        control,
        handleSubmit: formSubmit,
        formState: { isSubmitting },
        watch,
        reset,
    } = useForm<ResetPasswordFormData>({
        resolver: zodResolver(resetPasswordSchema),
        defaultValues: {
            password: '',
            confirmPassword: '',
        },
        mode: 'onChange',
    });

    const password = watch('password');

    useEffect(() => {
        // Get code directly from URL search params
        const code = searchParams.get('oobCode');
        console.log('ResetPasswordPage - oobCode from searchParams:', code);
        
        if (!code) {
            console.error('No oobCode found in URL parameters');
            setErrorKey('auth.errors.invalidActionCode');
            return;
        }
        
        setOobCode(code);
        
        // Verify the action code is valid
        const verifyCode = async () => {
            try {
                setLoading(true);
                const auth = getAuth();
                console.log('Verifying code:', code);
                const email = await verifyPasswordResetCode(auth, code);
                console.log('Code verified for email:', email);
                setEmail(email);
                setEmailVerified(true);
            } catch (err: any) {
                console.error('Error verifying reset code:', err);
                setErrorKey(formatFirebaseAuthError(err));
            } finally {
                setLoading(false);
            }
        };
        
        verifyCode();
    }, [searchParams, t]);

    // Handle the redirect progress animation
    useEffect(() => {
        let timer: NodeJS.Timeout;
        const redirectTime = 3000; // Reduced to 3 seconds for redirect
        const interval = 100; // Update progress every 100ms
        
        if (isRedirecting) {
            timer = setInterval(() => {
                setRedirectProgress((prev) => {
                    const newProgress = prev + (100 * interval / redirectTime);
                    
                    if (newProgress >= 100) {
                        clearInterval(timer);
                        navigate('/dashboard');
                        return 100;
                    }
                    
                    return newProgress;
                });
            }, interval);
        }
        
        return () => {
            if (timer) clearInterval(timer);
        };
    }, [isRedirecting, navigate]);

    const handleSubmit = async (data: ResetPasswordFormData) => {
        if (!oobCode) {
            setErrorKey('auth.errors.invalidActionCode');
            return;
        }
        
        try {
            setErrorKey(null);
            setSuccess('');
            setLoading(true);
            
            const auth = getAuth();
            await confirmPasswordReset(auth, oobCode, data.password);
            
            // Attempt to login with the new password
            if (email) {
                try {
                    await loginAfterReset(email, data.password);
                    setSuccess(t('auth.resetPassword.successAndLoggedIn'));
                } catch (loginErr: any) {
                    console.error('Error logging in after password reset:', loginErr);
                    setSuccess(t('auth.resetPassword.successButLoginFailed'));
                }
            } else {
                setSuccess(t('auth.resetPassword.success'));
            }
            
            reset();
            setIsRedirecting(true);
        } catch (err: any) {
            console.error('Error resetting password:', err);
            setErrorKey(formatFirebaseAuthError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthLayout 
            title={t('auth.resetPassword.title')}
            subtitle={t('auth.resetPassword.description')}
        >
            {errorKey && (
                <Alert 
                    severity="error" 
                    sx={{
                        marginBottom: '24px',
                        borderRadius: '8px',
                    }} 
                    role="alert" 
                    aria-live="assertive"
                >
                    {t(errorKey)}
                </Alert>
            )}
            
            {isRedirecting ? (
                <Fade in={true}>
                    <Box>
                        <Alert 
                            severity="success" 
                            sx={{
                                marginBottom: '16px',
                                borderRadius: '8px',
                                bgcolor: 'success.main',
                                color: '#ffffff',
                                '& .MuiAlert-icon': {
                                    color: '#ffffff'
                                },
                                '& .MuiAlert-message': {
                                    fontWeight: 500
                                }
                            }} 
                            role="status" 
                            aria-live="polite"
                        >
                            {success}
                        </Alert>
                        
                        <Box sx={{ width: '100%', mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                {t('common.redirecting')}...
                            </Typography>
                            <LinearProgress 
                                variant="determinate" 
                                value={redirectProgress} 
                                sx={{ 
                                    height: 8, 
                                    borderRadius: 4,
                                    backgroundColor: 'rgba(255, 107, 0, 0.2)',
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: '#FF6B00',
                                        borderRadius: 4,
                                    }
                                }}
                            />
                        </Box>
                    </Box>
                </Fade>
            ) : (
                <>
                    {success && !isRedirecting && (
                        <Alert 
                            severity="success" 
                            sx={{
                                marginBottom: '24px',
                                borderRadius: '8px',
                            }} 
                            role="status" 
                            aria-live="polite"
                        >
                            {success}
                        </Alert>
                    )}
                    
                    {emailVerified ? (
                        <Box 
                            component="form" 
                            onSubmit={formSubmit(handleSubmit)} 
                            sx={{
                                width: '100%',
                                marginTop: '16px',
                            }} 
                            noValidate
                        >
                            <Typography 
                                variant="body1" 
                                component="div"
                                sx={{ 
                                    mb: 3,
                                    textAlign: 'center' 
                                }}
                            >
                                {t('auth.resetPassword.resetFor')} <strong>{email}</strong>
                            </Typography>
                            
                            <Stack spacing={2}>
                                <FormTextField
                                    required
                                    fullWidth
                                    label={t('auth.password')}
                                    type="password"
                                    autoComplete="new-password"
                                    name="password"
                                    control={control}
                                    inputProps={{
                                        'aria-label': 'New password',
                                    }}
                                    disabled={isSubmitting || loading}
                                />
                                
                                <PasswordStrengthIndicator password={password} />
                                
                                <FormTextField
                                    required
                                    fullWidth
                                    label={t('auth.confirmPassword')}
                                    type="password"
                                    autoComplete="new-password"
                                    name="confirmPassword"
                                    control={control}
                                    inputProps={{
                                        'aria-label': 'Confirm new password',
                                    }}
                                    disabled={isSubmitting || loading}
                                    sx={{ 
                                        mb: 2 
                                    }}
                                />

                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    disabled={isSubmitting || loading}
                                    sx={{
                                        marginTop: '24px',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        fontSize: '1rem',
                                        backgroundColor: '#FF6B00',
                                        color: '#ffffff',
                                        height: '48px',
                                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
                                        '&:hover': {
                                            backgroundColor: '#FF7E33',
                                            boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.25)',
                                        },
                                    }}
                                    aria-label="Reset Password"
                                >
                                    {loading ? (
                                        <CircularProgress size={24} color="inherit" />
                                    ) : (
                                        t('auth.resetPassword.submitButton')
                                    )}
                                </Button>
                            </Stack>
                        </Box>
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 3 }}>
                            {loading ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                                    <CircularProgress size={24} sx={{ color: '#FF6B00' }} />
                                    <Typography>{t('auth.resetPassword.verifying')}</Typography>
                                </Box>
                            ) : (
                                <Typography color="error">{t('auth.resetPassword.invalidCode')}</Typography>
                            )}
                        </Box>
                    )}

                    {!isRedirecting && (
                        <Box sx={{
                            marginTop: '32px',
                            textAlign: 'center',
                        }}>
                            <Button
                                component={RouterLink}
                                to="/login"
                                variant="text"
                                startIcon={<ArrowBack />}
                                sx={{
                                    color: '#FF6B00',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: '0.95rem',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 107, 0, 0.08)',
                                    },
                                }}
                                aria-label="Back to Login"
                            >
                                {t('auth.resetPassword.backToLogin')}
                            </Button>
                        </Box>
                    )}
                </>
            )}
        </AuthLayout>
    );
};

export default ResetPasswordPage;
